import { Fragment, useMemo, useState, type FC, type ReactNode } from 'react';
import { LuFileText, LuThumbsDown, LuThumbsUp, LuArrowRight, LuEyeOff, LuTrophy, LuAudioLines, LuSquareArrowOutUpRight } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import AssistenteMarkdown, { respostaTemTabela } from './AssistenteMarkdown';
import AssistenteGrafico from './AssistenteGrafico';
import AssistenteAcao from './AssistenteAcao';
import AssistenteAchado from './AssistenteAchado';
import AssistenteSemTexto from './AssistenteSemTexto';
import AssistenteService, { type AcaoAssistente, type MensagemAssistente } from '../services/AssistenteService';
import aviso, { textoDoErro } from '../lib/aviso';

const hora = (iso: string) =>
    new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

/** Os tipos de achado que têm botão — e, com dois ou mais, barra de lote. */
type TipoAcionavel = 'ganha' | 'provavel' | 'etapa' | 'fora_de_venda';

/**
 * Uma mensagem da conversa com o assistente — a mesma na tela cheia
 * (`AssistentePage`) e no painel ao lado da tela (`AgentePainel`). Um
 * componente só para a bolha não existir em duas versões.
 */
const AssistenteMensagem: FC<{
    mensagem: MensagemAssistente;
    degradada?: boolean;
    onAvaliar?: (mensagem: MensagemAssistente, valor: 1 | -1) => void;
    onAcaoMudou?: (mensagemId: number, acao: AcaoAssistente) => void;
    /** Chamado quando um cartão leva para outra tela (o painel fecha). */
    onAbrirRegistro?: () => void;
    /** Quem fala, ao lado da resposta (o Brik no painel). Sem ele, a bolha fica sozinha, como na tela cheia. */
    avatar?: ReactNode;
    /**
     * É a última resposta da conversa? Só nela o Brik AI PERGUNTA se ajudou —
     * uma vez, no fim, como um colega perguntaria. Os polegares continuam em
     * todas, para quem quiser voltar e avaliar uma resposta antiga.
     */
    ultima?: boolean;
}> = ({ mensagem: m, degradada, onAvaliar, onAcaoMudou, onAbrirRegistro, avatar, ultima }) => {
    const navegar = useNavigate();
    const acoes = m.acoes ?? [];
    const achados = m.achados ?? [];
    const semTexto = m.semTexto ?? [];
    const atalhos = m.atalhos ?? [];

    /* Cada tipo tem a SUA barra e a sua ação: misturar "mover" com "tirar do
       funil" numa barra só seria um botão que faz duas coisas diferentes. E
       "a conversa diz que fechou" não se mistura com "parece fechada": são
       duas certezas diferentes, e a segunda a pessoa confere uma a uma. */
    const acionaveis = useMemo(() => ({
        ganha: achados.filter(a => a.tipo === 'ganha' && a.achadoId > 0),
        provavel: achados.filter(a => a.tipo === 'provavel' && a.achadoId > 0),
        etapa: achados.filter(a => a.tipo === 'etapa' && !!a.etapaSugerida && a.achadoId > 0),
        fora_de_venda: achados.filter(a => a.tipo === 'fora_de_venda' && a.achadoId > 0),
    }), [achados]);

    /* O valor corrigido no cartão mora aqui, e não lá dentro, porque a barra
       do lote precisa dele: marcar e fechar pela barra tem de usar o que a
       pessoa digitou. */
    const [valores, setValores] = useState<Record<number, number | null>>({});
    const [verSemTexto, setVerSemTexto] = useState(false);
    const [selecionados, setSelecionados] = useState<number[]>([]);

    /* A conversa REABERTA tem as ações gravadas, mas não a memória de qual
       cartão cada uma resolveu — e sem isto um cartão de venda já fechada
       voltava oferecendo "Dar como ganho". A ponte é a oportunidade: a rota da
       ação é /pipeline/deal/{id}, o mesmo id do achado. */
    const jaResolvidos = useMemo(() => {
        const comAcao = new Set(
            acoes
                .filter(a => a.estado === 'feita')
                .map(a => Number(a.rota?.match(/\/pipeline\/deal\/(\d+)/)?.[1]))
                .filter(id => Number.isFinite(id)),
        );
        return achados.filter(a => comAcao.has(a.oportunidadeId)).map(a => a.achadoId);
    }, [acoes, achados]);

    /* Os resolvidos NESTA sessão, pelo botão do cartão ou pelo lote. */
    const [feitosAgora, setFeitosAgora] = useState<number[]>([]);

    /* O que a barra e os cartões leem: o que já vinha resolvido do banco mais o
       que acabou de ser feito. Derivado, não copiado para o estado — as ações
       podem chegar depois do primeiro render. */
    const feitos = useMemo(
        () => [...new Set([...jaResolvidos, ...feitosAgora])],
        [jaResolvidos, feitosAgora],
    );
    const setFeitos = setFeitosAgora;
    const [emLote, setEmLote] = useState<string | null>(null);

    const sobram = (tipo: TipoAcionavel) =>
        acionaveis[tipo].filter(a => !feitos.includes(a.achadoId));

    const marcados = (tipo: TipoAcionavel) =>
        sobram(tipo).filter(a => selecionados.includes(a.achadoId)).map(a => a.achadoId);

    /* O valor que vale para cada cartão: o que a pessoa digitou, ou o que o
       Brik leu enquanto ela não mexeu. `in` em vez de `??` de propósito —
       APAGAR o campo grava null, e um `??` aqui ressuscitaria o valor do Brik
       num cartão que a pessoa deixou vazio de propósito. */
    const valorDe = (achadoId: number): number | null => achadoId in valores
        ? valores[achadoId]
        : (achados.find(a => a.achadoId === achadoId)?.valorSugerido ?? null);

    const aplicarEmLote = async (tipo: TipoAcionavel) => {
        const ids = marcados(tipo);
        setEmLote(tipo);
        try {
            const resultado = tipo === 'etapa'
                ? await AssistenteService.moverAchadosEmLote(ids)
                : tipo === 'fora_de_venda'
                    ? await AssistenteService.tirarDoFunilEmLote(ids)
                    : await AssistenteService.ganharAchadosEmLote(
                        /* Sem valor o servidor recusa, e uma recusa previsível não
                           precisa virar erro na tela: esses ficam no lugar, com a
                           nota pedindo o valor. */
                        ids.filter(id => valorDe(id)).map(id => ({ achadoId: id, valor: valorDe(id) })));

            /* Cada uma entra na conversa como ação própria, com o seu desfazer —
               o mesmo cartão de quem clica um a um. */
            for (const acao of resultado.movidas) onAcaoMudou?.(m.mensagemId, acao);
            const enviados = tipo === 'ganha' || tipo === 'provavel' ? ids.filter(id => valorDe(id)) : ids;
            setFeitos(atuais => [...atuais, ...enviados.filter(id => !resultado.falhas.some(f => f.achadoId === id))]);
            setSelecionados(atuais => atuais.filter(id => !ids.includes(id)));

            if (resultado.falhas.length > 0) {
                aviso.erro(`${resultado.falhas.length} não deu certo: ${resultado.falhas[0].motivo}`);
            }
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível aplicar nas selecionadas.'));
        } finally {
            setEmLote(null);
        }
    };

    const barra = (tipo: TipoAcionavel) => {
        const disponiveis = sobram(tipo);
        if (disponiveis.length < 2) return null;

        const escolhidos = marcados(tipo);
        /* Venda sem valor o servidor recusa. Em vez de travar o botão — que
           morre sem dizer por quê —, essas ficam de fora e a barra conta
           quantas são: a pessoa preenche o valor no cartão e clica de novo. */
        const semValor = (tipo === 'ganha' || tipo === 'provavel')
            ? escolhidos.filter(id => !valorDe(id)).length
            : 0;

        const quantos = escolhidos.length > 0 ? escolhidos.length : '';
        const vao = escolhidos.length - semValor;
        const rotulo = tipo === 'etapa'
            ? `Mover ${quantos} selecionada${escolhidos.length === 1 ? '' : 's'}`
            : tipo === 'fora_de_venda'
                ? `Tirar ${quantos} do funil`
                : `Dar ${vao > 0 ? vao : ''} como ganha${vao === 1 ? '' : 's'}`;

        return (
            <div className="agt-lote">
                <label className="agt-lote-todas">
                    <input
                        type="checkbox"
                        checked={escolhidos.length === disponiveis.length}
                        onChange={e => setSelecionados(ids => e.target.checked
                            ? [...ids, ...disponiveis.map(a => a.achadoId).filter(id => !ids.includes(id))]
                            : ids.filter(id => !disponiveis.some(a => a.achadoId === id)))}
                    />
                    <span>Marcar {disponiveis.length === 1 ? 'a' : `as ${disponiveis.length}`}</span>
                </label>

                {semValor > 0 && (
                    <span className="agt-lote-nota">
                        {semValor === 1 ? '1 marcada está sem valor e fica de fora' : `${semValor} marcadas estão sem valor e ficam de fora`}
                    </span>
                )}

                <button
                    type="button"
                    className="agt-lote-b"
                    disabled={escolhidos.length === 0 || emLote !== null || vao === 0}
                    onClick={() => aplicarEmLote(tipo)}
                >
                    {tipo === 'etapa'
                        ? <LuArrowRight size={14} />
                        : tipo === 'fora_de_venda' ? <LuEyeOff size={14} /> : <LuTrophy size={14} />}
                    {emLote === tipo ? 'Aplicando…' : rotulo}
                </button>
            </div>
        );
    };

    return (
        <div
            className={[
                'as-msg',
                m.papel === 'user' ? 'is-user' : 'is-model',
                degradada ? 'is-degradada' : '',
                avatar && m.papel === 'model' ? 'com-avatar' : '',
                /* Resposta com tabela, gráfico ou ação ganha a coluna inteira — ver `.is-larga`. */
                m.papel === 'model' && (respostaTemTabela(m.conteudo) || !!m.visual || acoes.length > 0 || achados.length > 0 || semTexto.length > 0) ? 'is-larga' : '',
            ].filter(Boolean).join(' ')}
        >
            {(() => {
                const bolha = (
                    <div className="as-bolha">
                        {/* A pergunta é texto literal do usuário; só a resposta é
                            formatada, porque só ela nasce do markdown que o prompt pede. */}
                        {m.papel === 'model'
                            ? <AssistenteMarkdown texto={m.conteudo} />
                            : m.conteudo}

                        {/* O gráfico vem da ferramenta que consultou o dado, não do
                            texto do modelo — por isso mora dentro da mesma bolha, logo
                            abaixo da leitura que o assistente escreveu. */}
                        {m.papel === 'model' && m.visual && (
                            <AssistenteGrafico visual={m.visual} />
                        )}
                    </div>
                );
                /* Com quem fala ao lado (o Brik, no painel), ele fica no pé da
                   bolha — o canto de onde a fala sai —, não no topo. */
                return avatar && m.papel === 'model'
                    ? <div className="as-bolha-linha">{avatar}{bolha}</div>
                    : bolha;
            })()}

            {/* As ações ficam fora da bolha, na ordem em que o assistente as
                fez: o cartão é o registro da ação, não parte do texto. */}
            {/* Os achados da varredura vêm antes das ações: eles são o
                resultado da pergunta, e o cartão de ação (quando houver) é o
                que o assistente fez depois. */}
            {m.papel === 'model' && achados.map((a, i) => {
                const tipo = a.achadoId > 0 ? (a.tipo as TipoAcionavel) : null;
                /* A barra do grupo vai logo DEPOIS do último cartão dele: o
                   gesto vem depois de olhar, e cada barra fala do seu grupo. */
                const fimDoGrupo = !!tipo && achados[i + 1]?.tipo !== a.tipo;

                return (
                    <Fragment key={a.achadoId > 0 ? a.achadoId : a.oportunidadeId}>
                        <AssistenteAchado
                            achado={a}
                            onMoveu={(acao, achadoId) => {
                                onAcaoMudou?.(m.mensagemId, acao);
                                setFeitos(atuais => atuais.includes(achadoId) ? atuais : [...atuais, achadoId]);
                            }}
                            movidaEmLote={feitos.includes(a.achadoId)}
                            selecionado={selecionados.includes(a.achadoId)}
                            valor={valorDe(a.achadoId)}
                            onValor={valor => setValores(atuais => ({ ...atuais, [a.achadoId]: valor }))}
                            onSelecionar={tipo && sobram(tipo).length > 1 && sobram(tipo).includes(a)
                                ? marcado => setSelecionados(ids => marcado ? [...ids, a.achadoId] : ids.filter(id => id !== a.achadoId))
                                : undefined}
                        />
                        {fimDoGrupo && barra(tipo)}
                    </Fragment>
                );
            })}

            {/* As ações ficam fora da bolha, na ordem em que o assistente as
                fez: o cartão é o registro da ação, não parte do texto. */}
            {/* As conversas que o Brik não pôde julgar não são cartão: elas
                viram UM link com o número, que abre a lista (pedido dele em
                25/10 — "melhor um link que abra uma modal do que aparecer a
                listagem de 40"). */}
            {m.papel === 'model' && semTexto.length > 0 && (
                <button type="button" className="agt-st-link" onClick={() => setVerSemTexto(true)}>
                    <LuAudioLines size={13} />
                    {semTexto.length === 1
                        ? 'Ver a conversa que não deu para julgar'
                        : `Ver as ${semTexto.length} conversas que não deram para julgar`}
                </button>
            )}

            {verSemTexto && (
                <AssistenteSemTexto conversas={semTexto} onFechar={() => setVerSemTexto(false)} />
            )}

            {m.papel === 'model' && acoes.map(a => (
                <AssistenteAcao
                    key={a.acaoId}
                    acao={a}
                    onMudou={nova => onAcaoMudou?.(m.mensagemId, nova)}
                    onAbrir={onAbrirRegistro}
                />
            ))}

            {/* O caminho escrito continua no texto — o botão é o atalho para
                quem não quer procurar. Pedido de 25/10: um cliente novo leu
                "vá em Configurações › ..." e perguntou "qual é esse
                configurações?". */}
            {m.papel === 'model' && atalhos.length > 0 && (
                <div className="as-atalhos">
                    {atalhos.map(a => (
                        <button
                            key={a.rota}
                            type="button"
                            className="as-atalho"
                            title={a.onde}
                            onClick={() => { navegar(a.rota); onAbrirRegistro?.(); }}
                        >
                            <LuSquareArrowOutUpRight size={13} /> Abrir {a.titulo}
                        </button>
                    ))}
                </div>
            )}

            {m.fontes.length > 0 && (
                <div className="as-fontes">
                    {m.fontes.map(f => (
                        <span key={f} className="as-fonte">
                            <LuFileText size={12} /> {f}
                        </span>
                    ))}
                </div>
            )}

            <div className="as-rodape">
                <span className="as-hora">{hora(m.criadaEm)}</span>

                {/* A pergunta aparece só na última resposta e só enquanto
                    ninguém respondeu: o polegar sozinho, cinza e sem rótulo,
                    ficou 100 respostas sem um clique. Perguntar é o que
                    transforma dois ícones num gesto. */}
                {m.papel === 'model' && m.mensagemId > 0 && onAvaliar && ultima && !m.avaliacao && (
                    <span className="as-aval-pede">Essa resposta ajudou?</span>
                )}

                {/* Só em resposta já gravada: a otimista (id negativo)
                    ainda não existe no servidor para receber a avaliação. */}
                {m.papel === 'model' && m.mensagemId > 0 && onAvaliar && (
                    <span className="as-aval" role="group" aria-label="Esta resposta foi útil?">
                        <button
                            type="button"
                            className={`as-aval-b ${m.avaliacao === 1 ? 'is-on' : ''}`}
                            aria-pressed={m.avaliacao === 1}
                            aria-label="Útil"
                            onClick={() => onAvaliar(m, 1)}
                        >
                            <LuThumbsUp size={13} />
                        </button>
                        <button
                            type="button"
                            className={`as-aval-b ${m.avaliacao === -1 ? 'is-on' : ''}`}
                            aria-pressed={m.avaliacao === -1}
                            aria-label="Não útil"
                            onClick={() => onAvaliar(m, -1)}
                        >
                            <LuThumbsDown size={13} />
                        </button>
                    </span>
                )}

                {/* Quem respondeu merece saber que foi ouvido — e o aviso
                    substitui a pergunta, em vez de empilhar com ela. */}
                {m.papel === 'model' && !!m.avaliacao && ultima && (
                    <span className="as-aval-obrigado">
                        {m.avaliacao === 1 ? 'Que bom. Obrigado!' : 'Obrigado — vamos melhorar isso.'}
                    </span>
                )}
            </div>
        </div>
    );
};

export default AssistenteMensagem;
